/**
 * Dinero en ElDescuadre.
 *
 * DECISIÓN DE DISEÑO (A6 del plan): el peso colombiano no usa centavos en la
 * práctica, así que todo el sistema trabaja con **pesos enteros**, nunca con
 * decimales flotantes. Un `Pesos` es siempre un número entero.
 *
 * Esto elimina de raíz los errores de redondeo de `0.1 + 0.2 !== 0.3` y hace
 * que sumar mil movimientos dé exactamente el mismo resultado que sumarlos en
 * la base de datos.
 *
 * En la base de datos se guarda como BIGINT (int8) y viaja al cliente como
 * número entero, que es exacto en JavaScript hasta 9.007.199.254.740.991
 * (unos nueve mil billones de pesos): muchísimo más de lo que puede llegar a
 * sumar cualquier patrimonio personal.
 */
/** Límite de seguridad de los enteros en JavaScript (2^53 - 1). */
export const MAX_SAFE_PESOS = Number.MAX_SAFE_INTEGER;
/** Error de validación de dinero. */
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MoneyError';
    }
}
/** ¿Es un valor de dinero válido? Entero, finito y dentro del rango seguro. */
export function isPesos(value) {
    return (typeof value === 'number' &&
        Number.isFinite(value) &&
        Number.isInteger(value) &&
        Math.abs(value) <= MAX_SAFE_PESOS);
}
/** Lanza si el valor no es un importe válido. Se usa en las fronteras del sistema. */
export function assertPesos(value, label = 'importe') {
    if (!isPesos(value)) {
        throw new MoneyError(`El ${label} debe ser un número entero de pesos; se recibió: ${String(value)}`);
    }
}
/**
 * Convierte a pesos enteros redondeando al peso más cercano.
 * Acepta números con decimales y cadenas numéricas.
 * Lanza si el valor no es un número finito.
 */
export function toPesos(value) {
    const n = typeof value === 'number' ? value : Number(value.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(n)) {
        throw new MoneyError(`No se puede convertir a pesos: ${String(value)}`);
    }
    const rounded = Math.round(n);
    if (Math.abs(rounded) > MAX_SAFE_PESOS) {
        throw new MoneyError('El importe supera el máximo representable con seguridad.');
    }
    return rounded;
}
/** Suma de importes, verificando que no haya desbordamiento. */
export function sumPesos(values) {
    let total = 0;
    for (const v of values) {
        assertPesos(v);
        total += v;
    }
    if (Math.abs(total) > MAX_SAFE_PESOS) {
        throw new MoneyError('La suma de importes desborda el rango seguro.');
    }
    return total;
}
/**
 * Multiplica un importe por un factor (por ejemplo, para la TRM) redondeando al peso.
 * Se usa para conversión de moneda, donde sí puede haber decimales intermedios.
 */
export function multiplyPesos(amount, factor) {
    assertPesos(amount);
    if (!Number.isFinite(factor)) {
        throw new MoneyError('El factor de conversión no es un número válido.');
    }
    return toPesos(amount * factor);
}
/** Reparte un importe en `parts` partes iguales, sin perder ni un peso por redondeo. */
export function splitPesos(amount, parts) {
    assertPesos(amount);
    if (!Number.isInteger(parts) || parts <= 0) {
        throw new MoneyError('El número de partes debe ser un entero positivo.');
    }
    const base = Math.trunc(amount / parts);
    const remainder = amount - base * parts;
    const result = [];
    for (let i = 0; i < parts; i += 1) {
        result.push(i < Math.abs(remainder) ? base + Math.sign(remainder) : base);
    }
    return result;
}
/** Formato de moneda colombiana de uso corriente: $1.234.567 */
export function formatCOP(amount, options = {}) {
    const { withSymbol = true } = options;
    assertPesos(amount);
    const formatted = new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.abs(amount));
    const sign = amount < 0 ? '-' : '';
    return withSymbol ? `${sign}$${formatted}` : `${sign}${formatted}`;
}
/**
 * Formato abreviado para tarjetas y gráficos, en la jerga colombiana:
 *   $15 mil  ·  $2,5 M  ·  $1,2 MM  ·  $3,4 B
 */
export function formatCompactCOP(amount) {
    assertPesos(amount);
    const sign = amount < 0 ? '-' : '';
    const abs = Math.abs(amount);
    const cut = (value) => new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(value);
    if (abs >= 1e12)
        return `${sign}$${cut(abs / 1e12)} B`;
    if (abs >= 1e9)
        return `${sign}$${cut(abs / 1e9)} MM`;
    if (abs >= 1e6)
        return `${sign}$${cut(abs / 1e6)} M`;
    if (abs >= 1e3)
        return `${sign}$${cut(abs / 1e3)} mil`;
    return `${sign}$${abs}`;
}
/**
 * Un importe junto a su multiplicador: «14k», «2,5m», «950 mil», «3 millones».
 *
 * El multiplicador va **pegado a su número y en cualquier parte de la frase**, no
 * solo al final. Eso importa porque la gente escribe «2,5m de arriendo» o «14k de
 * taxi», y con la comprobación antigua —que exigía el sufijo al final— el «m» no
 * se reconocía, la coma se interpretaba como pesos y «2,5m» acababa valiendo
 * **3 pesos**. Un error así, en una aplicación de dinero, es grave.
 *
 * Las alternativas van de la más larga a la más corta para que «millones» gane a
 * «mil» y «mil» gane a «m».
 */
const AMOUNT_WITH_SUFFIX = /(\d[\d.,]*\d|\d)\s*(millones|millon|mm|mil|m|k)\b/i;
/** El primer número reconocible, con separadores de miles o decimales. */
const PLAIN_NUMBER = /\d[\d.,]*\d|\d/;
/** Cuánto multiplica cada sufijo. */
const SUFFIX_FACTORS = {
    millones: 1_000_000,
    millon: 1_000_000,
    mm: 1_000_000,
    m: 1_000_000,
    mil: 1_000,
    k: 1_000,
};
/**
 * Interpreta la parte numérica, con las convenciones colombianas.
 * Devuelve el número sin redondear, para no perder los decimales antes de
 * aplicar el multiplicador.
 */
function parseNumericPart(rawInput) {
    let raw = rawInput.replace(/\s/g, '');
    if (!raw)
        return null;
    const hasDot = raw.includes('.');
    const hasComma = raw.includes(',');
    if (hasDot && hasComma) {
        // El separador decimal es el último que aparece; el otro es de miles.
        raw =
            raw.lastIndexOf(',') > raw.lastIndexOf('.')
                ? raw.replace(/\./g, '').replace(',', '.')
                : raw.replace(/,/g, '');
    }
    else if (hasComma) {
        // Convención colombiana: la coma es decimal.
        raw = raw.replace(/,/g, '.');
        const decimals = raw.split('.')[1];
        // "1,500" escrito a la inglesa: tres dígitos exactos y nada más detrás.
        if (decimals && decimals.length === 3 && !raw.startsWith('0')) {
            raw = raw.replace('.', '');
        }
    }
    else if (hasDot) {
        const segments = raw.split('.');
        const last = segments[segments.length - 1] ?? '';
        const allGroupsOfThree = segments.slice(1).every((s) => s.length === 3);
        // "15.000" son miles; "2.5" es decimal.
        raw = last.length === 3 && allGroupsOfThree ? segments.join('') : String(Number(raw));
    }
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}
/**
 * Interpreta un importe escrito por una persona, con las convenciones colombianas.
 * Entiende: `15.000` · `$15.000` · `15000` · `12.500,00` · `14k` · `14 mil` ·
 * `2,5m` · `2.5 millones` · `1.234.567`, y todo eso **en medio de una frase**
 * («papitas 3.500 del descanso», «2,5m de arriendo»).
 *
 * Devuelve `null` si no hay ningún importe reconocible (nunca inventa un cero,
 * porque un cero silencioso en finanzas es peor que un error visible).
 */
export function parseAmountFromText(input) {
    if (!input)
        return null;
    const text = input.toLowerCase().trim();
    if (!text)
        return null;
    // 1. Primero, un importe con multiplicador, esté donde esté.
    const suffixed = AMOUNT_WITH_SUFFIX.exec(text);
    if (suffixed) {
        const base = parseNumericPart(suffixed[1] ?? '');
        const factor = SUFFIX_FACTORS[(suffixed[2] ?? '').toLowerCase()] ?? 1;
        if (base !== null)
            return toPesos(base * factor);
    }
    // 2. Si no lo hay, el primer número que aparezca.
    const plain = PLAIN_NUMBER.exec(text);
    if (!plain)
        return null;
    const value = parseNumericPart(plain[0]);
    return value === null ? null : toPesos(value);
}
//# sourceMappingURL=money.js.map